<script setup lang="ts">
import { computed } from 'vue';
import { DraggableArea, BoundingBox, StencilPreview } from 'vue-advanced-cropper';
import { useOverlay, type OverlayData } from '@/composables/useOverlay';

const emit = defineEmits(['resize', 'resizeEnd', 'move', 'moveEnd'])

interface Handlers {
  eastNorth?: boolean
  north?: boolean
  westNorth?: boolean
  west?: boolean
  westSouth?: boolean
  south?: boolean
  eastSouth?: boolean
  east?: boolean
}

// Define props expected from vue-advanced-cropper
interface StencilProps {
  image: {
    width: number;
    height: number;
    src: string;
  };
  coordinates: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  stencilCoordinates: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  movable?: boolean;
  resizable?: boolean,
  handlers?: Handlers
  // Optional — lets you keep overlay toggle flexible
  overlay?: boolean;
  overlayData: OverlayData
}

const props = withDefaults(defineProps<StencilProps>(), {
  movable: true,
  resizable: true,
  handlers: () => {
    return {
      eastNorth: true,
      north: true,
      westNorth: true,
      west: true,
      westSouth: true,
      south: true,
      eastSouth: true,
      east: true,
    }
  },
});

// Reactive styles for the stencil container to match the cropper's dimensions
const stencilStyles = computed(() => {
  const { height, width, left, top } = props.stencilCoordinates;
  return {
    width: `${width}px`,
    height: `${height}px`,
    transform: `translate(${left}px, ${top}px)`,
  }
});


function onResize(resizeEvent: Event) {
  if (!props.resizable) return
  emit('resize', resizeEvent)
}

function onResizeEnd() {
  if (!props.resizable) return
  emit('resizeEnd')
}

function onMove(moveEvent: Event) {
  if (!props.movable) return
  emit('move', moveEvent);
}

function onMoveEnd() {
  if (!props.movable) return
  emit('moveEnd');
}

const stencilSize = computed(() => ({
  width: props.stencilCoordinates.width,
  height: props.stencilCoordinates.height,
}));
const { svgTemplate } = useOverlay(stencilSize, props.overlayData);


</script>

<template>
  <div class="custom-stencil" :style="stencilStyles">
    <bounding-box ref="boundingBoxRef" :resizable="resizable" :handlers="handlers" @resize="onResize"
      @resize-end="onResizeEnd">
      <draggable-area :movable="movable" @move="onMove" @moveEnd="onMoveEnd">

        <stencil-preview :image="image" :width="stencilCoordinates.width" :height="stencilCoordinates.height"
          :coordinates="coordinates" />

        <div v-if="overlay" class="overlay-svg-container" v-html="svgTemplate" :width="stencilCoordinates.width"
          :height="stencilCoordinates.height"></div>

      </draggable-area>
    </bounding-box>
  </div>
</template>

<style>
.custom-stencil {
  position: absolute;
  /* Ensure this container is non-interactive for pointer events */
  /* pointer-events: none; */
  /* No background here, the main cropper handles the masked image */
  /* overflow: hidden; */
  /* Important for SVG content that might draw outside viewBox */
}

.cropper-area-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* This creates the transparent 'window' effect inside the stencil */
  /* This specific style is often controlled by vue-advanced-cropper itself if it's a fixed stencil */
  /* For custom stencils, you can make it completely transparent or lightly tinted */
  /* background-color: rgba(0, 0, 0, 0.3); */
  border: 2px dashed rgba(255, 255, 255, 0.7);
  box-sizing: border-box;
}

.overlay-svg-container {
  /* Ensure svg is non-interactive for pointer events */
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  /* Use 100% width/height to make the SVG fill the stencil's area */
  /* width: 100%;
  height: 100%; */
  /* Ensure SVG content is on top */
  z-index: 10;
  /* Make sure text and images are sharp  NOT sure we should do this*/
  /* font-smoothing: antialiased;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility; */
}

/* Example styling for SVG elements */
.overlay-svg text {
  font-family: sans-serif;
  font-weight: bold;
}
</style>
